import type Alpine from 'alpinejs';

import {type AlpineMachine, createAlpineMachine, defineAlpineData, type MachineSnapshot, type TrellisAlpineComponent} from '@client/runtime';
import {syncControlPresence, syncNativeControl} from '@client/runtime/form-control';
import {addListener, setPresence, type CleanupStack} from '@client/utils';
import {inputSchema, type InputProps} from './contract';
import {createInputMachine, inputHooks, type InputContext} from './machine';

/** Public Alpine API for `ui.input` and `ui.input.control`. @public */
interface UiInputApi {
    /** Current native input value. */
    readonly value: string;
    /** Clear the input, emit native input/change events, and restore focus. */
    clear(): void;
    /** Copy the current value to the clipboard. */
    copy(): void;
}

type InputData = AlpineMachine<InputContext, InputProps, typeof inputHooks> & UiInputApi & {
    init(): void;
    destroy(): void;
};

const UiInput = (): TrellisAlpineComponent<InputData> => {
    let rootEl: (HTMLElement & Alpine.ElementWithXAttributes) | null = null;
    let inputEl: HTMLInputElement | null = null;
    let listeners: CleanupStack = [];
    let copiedTimer: number | null = null;
    let dispatching = false;

    const root = (): HTMLElement => {
        if (!rootEl) throw new Error('Input has not been initialized.');
        return rootEl;
    };
    const input = (): HTMLInputElement => {
        if (!inputEl) throw new Error('Input requires a native input element.');
        return inputEl;
    };
    const snapshot = (): MachineSnapshot<InputContext, InputProps> => bridge.getCommittedSnapshot();
    const syncDom = (): void => {
        const current = snapshot();
        const props = current.props;
        const native = input();
        if (native.value !== current.context.value) native.value = current.context.value;
        native.type = props.type;
        if (props.placeholder) native.placeholder = props.placeholder;
        else native.removeAttribute('placeholder');
        syncNativeControl({input: native, state: {
            name: props.name,
            disabled: props.disabled,
            readonly: props.readonly,
            required: props.required,
            invalid: props.invalid,
        }});
        syncControlPresence({el: root(), state: props, value: 'true'});
        const control = root().querySelector<HTMLElement>('[data-ui-input-control]');
        if (control) syncControlPresence({el: control, state: {disabled: props.disabled, invalid: props.invalid}, value: 'true'});
        root().querySelectorAll<HTMLElement>('[data-ui-input-clear]').forEach((button) => {
            button.hidden = current.context.value.length === 0;
            if (button instanceof HTMLButtonElement) button.disabled = props.disabled || props.readonly;
        });
        root().querySelectorAll<HTMLElement>('[data-ui-input-copy]').forEach((button) => {
            if (button instanceof HTMLButtonElement) button.disabled = props.disabled;
            setPresence({el: button, attr: 'data-copied', present: current.tags.includes('copied'), value: 'true'});
        });
        setPresence({el: root(), attr: 'data-copied', present: current.tags.includes('copied'), value: 'true'});
    };
    const dispatchNative = (type: 'input' | 'change'): void => {
        dispatching = true;
        try { input().dispatchEvent(new Event(type, {bubbles: true})); } finally { dispatching = false; }
    };
    const bridge = createAlpineMachine({
        schema: inputSchema,
        hooks: inputHooks,
        createMachine: ({props}) => createInputMachine({props, effects: {
            sync: syncDom,
            copy: (scope) => {
                const write = navigator.clipboard?.writeText(scope.context.value) ?? Promise.resolve();
                void write.then(() => bridge.send('COPY_DONE')).catch(() => bridge.send('COPY_ERROR'));
            },
            emitInput: () => dispatchNative('input'),
            emitChange: () => dispatchNative('change'),
            focus: () => input().focus({preventScroll: true}),
            startCopiedTimer: () => {
                if (copiedTimer !== null) window.clearTimeout(copiedTimer);
                copiedTimer = window.setTimeout(() => bridge.send('COPIED_TIMEOUT'), 1200);
            },
        }}),
    });

    return {
        ...bridge,
        init(): void {
            rootEl = this.$el;
            inputEl = root().querySelector<HTMLInputElement>('[data-ui-input]');
            if (!inputEl) throw new Error('Input requires a native input element.');
            bridge.connect(rootEl, this.snapshot);
            addListener({target: inputEl, event: 'input', handler: () => {
                if (!dispatching) bridge.send({type: 'VALUE_CHANGED', value: input().value});
            }, cleanup: listeners});
            addListener({target: rootEl, event: 'click', handler: (event) => {
                const target = event.target;
                if (!(target instanceof Element)) return;
                if (target.closest('[data-ui-input-clear]')) {
                    event.preventDefault();
                    bridge.send('CLEAR');
                } else if (target.closest('[data-ui-input-copy]')) {
                    event.preventDefault();
                    bridge.send('COPY');
                }
            }, cleanup: listeners});
            syncDom();
        },
        destroy(): void {
            if (copiedTimer !== null) window.clearTimeout(copiedTimer);
            copiedTimer = null;
            listeners.splice(0).reverse().forEach((cleanup) => cleanup());
            bridge.disconnect();
            rootEl = null;
            inputEl = null;
        },
        get value(): string {
            return bridge.getCommittedSnapshot().context.value;
        },
        clear(): void {
            if (!input().disabled && !input().readOnly) bridge.send('CLEAR');
        },
        copy(): void {
            if (!input().disabled) bridge.send('COPY');
        },
    };
};

const registerInput = defineAlpineData('UiInput', UiInput);

export {registerInput, UiInput};
export type {InputData, UiInputApi};
